import { ChoiceRecordStorageObjectEventType, StorageObjectEventType } from '@navio/nsdk';
import type { NoddiIconName } from 'noddi-ui-common';
import {
  NoddiDateFormats,
  noddiFormatDate,
  storageEventCopy,
  storageEventTypeLabel,
  storageEventTypeLabels
} from 'noddi-util';

interface EventTypeStyle {
  label: string;
  icon: NoddiIconName;
  className: string;
}

// Labels: the product words from noddi-util. Soft fill: border matches bg so each pill reads as a tint with no outline.
export const STORAGE_EVENT_TYPE_STYLE: Record<StorageObjectEventType, EventTypeStyle> = {
  PICKED_UP_FROM_CUSTOMER: {
    label: storageEventTypeLabels.PICKED_UP_FROM_CUSTOMER,
    icon: 'Handshake',
    className: 'border-blue-50 bg-blue-50 text-blue-800'
  },
  MOUNTED_ON_CAR: {
    label: storageEventTypeLabels.MOUNTED_ON_CAR,
    icon: 'Car',
    className: 'border-teal-50 bg-teal-50 text-teal-800'
  },
  PICKED_UP_BY_CUSTOMER: {
    label: storageEventTypeLabels.PICKED_UP_BY_CUSTOMER,
    icon: 'Handshake',
    className: 'border-amber-50 bg-amber-50 text-amber-900'
  },
  DELIVERED_TO_CUSTOMER: {
    label: storageEventTypeLabels.DELIVERED_TO_CUSTOMER,
    icon: 'CheckCircle',
    className: 'border-emerald-50 bg-emerald-50 text-emerald-800'
  },
  LOADED_ONTO_VEHICLE: {
    label: storageEventTypeLabels.LOADED_ONTO_VEHICLE,
    icon: 'Truck',
    className: 'border-amber-50 bg-amber-50 text-amber-900'
  },
  INTERNAL_PICKUP: {
    label: storageEventTypeLabels.INTERNAL_PICKUP,
    icon: 'PackageMinus',
    className: 'border-gray-50 bg-gray-50 text-gray-800'
  },
  INTERNAL_DELIVERY: {
    label: storageEventTypeLabels.INTERNAL_DELIVERY,
    icon: 'PackagePlus',
    className: 'border-slate-50 bg-slate-50 text-slate-800'
  },
  PICKED_UP_FROM_SUPPLIER: {
    label: storageEventTypeLabels.PICKED_UP_FROM_SUPPLIER,
    icon: 'Warehouse',
    className: 'border-violet-50 bg-violet-50 text-violet-800'
  },
  MOUNTED_TIRE_ON_WHEEL: {
    label: storageEventTypeLabels.MOUNTED_TIRE_ON_WHEEL,
    icon: 'Wrench',
    className: 'border-rose-50 bg-rose-50 text-rose-800'
  },
  EVENT_RETRACTED: {
    label: storageEventTypeLabels.EVENT_RETRACTED,
    icon: 'Undo2',
    className: 'border-slate-50 bg-slate-50 text-slate-800'
  },
  DETAILS_CHANGED: {
    label: storageEventTypeLabels.DETAILS_CHANGED,
    icon: 'FileText',
    className: 'border-slate-50 bg-slate-50 text-slate-800'
  },
  LABEL_PRINTED: {
    label: storageEventTypeLabels.LABEL_PRINTED,
    icon: 'Printer',
    className: 'border-slate-50 bg-slate-50 text-slate-800'
  },
  LABEL_REPLACED: {
    label: storageEventTypeLabels.LABEL_REPLACED,
    icon: 'Tag',
    className: 'border-slate-50 bg-slate-50 text-slate-800'
  },
  LOST: {
    label: storageEventTypeLabels.LOST,
    icon: 'TriangleAlert',
    className: 'border-red-50 bg-red-50 text-red-800'
  },
  DISCARDED: {
    label: storageEventTypeLabels.DISCARDED,
    icon: 'Trash2',
    className: 'border-neutral-50 bg-neutral-50 text-neutral-800'
  }
};

const LAST_EVENT_ACTION: Record<StorageObjectEventType, string> = {
  PICKED_UP_FROM_CUSTOMER: storageEventCopy.pickedUpFromCustomer.past,
  MOUNTED_ON_CAR: storageEventCopy.mountedOnCar.past,
  PICKED_UP_BY_CUSTOMER: storageEventCopy.pickedUpByCustomer.past,
  DELIVERED_TO_CUSTOMER: storageEventCopy.deliveredToCustomer.past,
  LOADED_ONTO_VEHICLE: 'loaded onto a vehicle',
  INTERNAL_PICKUP: storageEventCopy.removeFromStorageUnit.past,
  INTERNAL_DELIVERY: storageEventCopy.moveToStorageUnit.past,
  PICKED_UP_FROM_SUPPLIER: 'picked up from the supplier',
  MOUNTED_TIRE_ON_WHEEL: 'mounted on rims',
  LABEL_PRINTED: 'labeled',
  LABEL_REPLACED: 'put on the new label',
  EVENT_RETRACTED: 'undone',
  DETAILS_CHANGED: 'updated storage details',
  LOST: 'marked lost',
  DISCARDED: 'discarded'
};

export const lastStorageEventSentence = (event: {
  eventType: ChoiceRecordStorageObjectEventType;
  actorName: string;
  occurredAt: string;
}): string => {
  const action = LAST_EVENT_ACTION[event.eventType.value] ?? storageEventTypeLabel(event.eventType).toLowerCase();
  const when = noddiFormatDate(event.occurredAt, NoddiDateFormats.DATE_TIME);
  const by = event.actorName ? ` by ${event.actorName}` : '';
  return `Last ${action}${by} at ${when}`;
};
